import { useLayoutEffect } from "react";


const USERBACK_WIDGET_ID = "userback_button_container";

/**
 * Reparent the Userback widget into the sidebar anchor while the sidebar
 * is mounted, and restore it to <body> on unmount.
 *
 * Why `useLayoutEffect`: cleanup runs synchronously BEFORE React detaches
 * the sidebar subtree from the DOM, so we can safely move the widget out
 * to <body> first. With `useEffect`, the parent is already gone by the
 * time cleanup runs and the widget gets removed with it — meaning on the
 * next login the SDK (which only injects once per page) has nothing to
 * re-show.
 */
export const useUserbackInAppMenu = (
	anchorRef: React.RefObject<HTMLDivElement | null>,
	isCollapsed: boolean,
) => {
	useLayoutEffect(() => {
		const anchor = anchorRef.current;
		if (!anchor || isCollapsed) {
			return;
		}

		const adopt = () => {
			const widget = document.getElementById(USERBACK_WIDGET_ID);
			if (!widget || widget.parentElement === anchor) {
				return;
			}
			anchor.appendChild(widget);
			// Reset any fixed-position styling the SDK applied so the widget
			// flows inside the anchor.
			widget.style.position = "";
			widget.style.top = "";
			widget.style.left = "";
			widget.style.right = "";
			widget.style.bottom = "";
			widget.style.display = "";
		};

		adopt();

		// The SDK injects asynchronously; watch <body> for the widget arriving.
		const observer = new MutationObserver(adopt);
		observer.observe(document.body, { childList: true });

		return () => {
			observer.disconnect();
			// Move the widget back to <body> BEFORE React tears down the sidebar
			// subtree (this runs synchronously thanks to useLayoutEffect). Keeps
			// the widget alive for the next mount (e.g. post-login).
			const widget = document.getElementById(USERBACK_WIDGET_ID);
			if (widget && widget.parentElement !== document.body) {
				document.body.appendChild(widget);
			}
		};
	}, [anchorRef, isCollapsed]);
};